'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslations } from 'next-intl';
import { apiPaths } from '@/constants/apiPaths';
import Button from '@/ui/components/shared/Button';
import Spinner from '@/ui/components/shared/Spinner/Spinner';
import { ValueValidateInput } from '@/ui/components/shared/inputs/ValueValidateInput';
import { AddedValuePill } from '@/ui/components/shared/inputs/ValueValidateInput/AddedValuePill';
import { NukiIdSubtext } from '@/ui/components/pages/ProductDetail/NukiIdOverlay/nuki-id-subtext/nuki-id-subtext';
import { useNukiIdEntry } from '@/ui/hooks/productDetail/use-nuki-id-entry';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { DigitalProduct } from '@/ui/services/DigitalProductService';
import { Subscription } from '@/ui/services/SubscriptionService';
import { useCartStore } from '@/ui/store/cart';
import formatPunctuation from '@/ui/utils/formatPunctuation';
import { nukiIdValidation } from '@/ui/utils/nuki-id-validation';
import { DocShapeId } from '@/utils/shapeIds';
import { ClassicPdpUpsellProduct } from '../../classic-pdp-content';
import { getUpsellServiceData, UpsellServiceData } from '../upsell-cards-actions';

export interface ServiceNukiIdModalProps {
  product: ClassicPdpUpsellProduct;
  open: boolean;
  onClose: () => void;
}

type ServiceLoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; service: UpsellServiceData };

type ServiceProduct = Subscription | DigitalProduct;

// Rich-text tags receive their content as a list of parts; `toString()` on that
// list would join the parts with commas.
const chunksToText = (chunks: React.ReactNode) =>
  React.Children.toArray(chunks)
    .filter((chunk): chunk is string | number => ['string', 'number'].includes(typeof chunk))
    .join('');

// Markup of the `instructions` message, the same as in the drawer of the
// product page. Defined at module level, so no render creates new tag components.
const INSTRUCTION_TAGS = {
  b: (chunks: React.ReactNode) =>
    chunks && <span className="font-bold">{formatPunctuation(chunksToText(chunks))}</span>,
  gray: (chunks: React.ReactNode) => chunks && <span className="text-neutral-55">{chunks}</span>,
};

interface ServiceNukiIdFormProps {
  service: ServiceProduct;
  validationPath: string;
  onClose: () => void;
}

/**
 * Body of the modal once the service has loaded: the Smart Lock ID field, the
 * IDs added so far, the price and the add-to-cart action. The service sells
 * per ID, so the action stays disabled until at least one ID applies.
 */
function ServiceNukiIdForm({ service, validationPath, onClose }: Readonly<ServiceNukiIdFormProps>) {
  const t = useTranslations('NukiIdOverlay');
  const tChooser = useTranslations('ProductChooser');
  const { setShowNotifyMe } = useCartStore();
  const { formatSubscriptionPrice, formatCurrency } = useCurrencyFormatter();
  const [nukiIds, setNukiIds] = useState<string[]>([]);

  // The default variant, as on the product page of the service. The URL `sku`
  // belongs to the product of the page, so the modal never reads or writes it.
  const variant = useMemo(() => service.getDefaultVariant(), [service]);

  const nukiIdEntry = useNukiIdEntry({
    nukiIds,
    setNukiIds,
    sku: variant?.sku ?? '',
    apiPath: validationPath,
    isValidId: nukiIdValidation,
  });

  if (!variant) {
    return <p className="mt-4 text-sm text-red">{t('Unknown Error has occurred')}</p>;
  }

  const isAvailable = service.isVariantInStock(variant);
  const recurringPrice = variant.subscriptionPlan?.periods[0]?.recurring;
  const price = recurringPrice
    ? formatSubscriptionPrice(recurringPrice)
    : variant.priceVariant && formatCurrency(variant.priceVariant.price * (nukiIds.length || 1));

  const addToCart = () => {
    service.addToCart(variant, { nukiIds });
    setNukiIds([]);
    onClose();
  };

  // The notify-me modal is a dialog of its own, so this one closes first
  // instead of trapping the focus that the notify-me form needs.
  const notifyMe = () => {
    onClose();
    setShowNotifyMe(true, variant, null);
  };

  return (
    <>
      <div className="mt-4 text-base">{t.rich('instructions', INSTRUCTION_TAGS)}</div>

      {isAvailable && (
        <>
          <ValueValidateInput
            onApply={nukiIdEntry.handleApplyNukiId}
            validationError={nukiIdEntry.validationError}
            label="Smart Lock Id"
            buttonText={t('Add')}
            placeholder="Smart Lock ID"
            pending={nukiIdEntry.loading}
          />
          <NukiIdSubtext subtext={service.nukiIdSubtext} />
          <div className="mt-2 flex flex-wrap gap-2">
            {nukiIds.map((id) => (
              <AddedValuePill
                key={id}
                value={id}
                onRemove={nukiIdEntry.handleRemoveNukiId}
                actionLabel={t('Remove Nuki ID')}
              />
            ))}
          </div>
        </>
      )}

      {price && (
        <p className="mt-6 flex items-baseline justify-between">
          <span className="font-medium">{tChooser('Subtotal')}</span>
          <span className="font-bold">
            {price}
            <span className="ml-1 text-sm font-normal text-neutral-55">{tChooser('incl VAT')}</span>
          </span>
        </p>
      )}

      {isAvailable ? (
        <Button className="mt-4 w-full" onClick={addToCart} disabled={nukiIds.length === 0}>
          {tChooser('Add to cart')}
        </Button>
      ) : (
        <Button className="mt-4 w-full" onClick={notifyMe}>
          {tChooser('Notify me')}
        </Button>
      )}
    </>
  );
}

interface ServiceProductSetup {
  product: ServiceProduct;
  validationPath: string;
}

/** Builds the service instance the cart needs and the path that validates its IDs. */
function useServiceProduct(state: ServiceLoadState) {
  const cartStore = useCartStore();

  return useMemo<ServiceProductSetup | null>(() => {
    if (state.status !== 'ready') {
      return null;
    }

    const { service } = state;

    if (service.shapeIdentifier === DocShapeId.subscription) {
      return {
        product: new Subscription(service.data, cartStore),
        validationPath: apiPaths.devices.smartHosting.validate,
      };
    }

    const digitalProduct = new DigitalProduct(service.data, cartStore);

    return {
      product: digitalProduct,
      validationPath: digitalProduct.getValidationPath(digitalProduct.getProductTypeByPath()),
    };
    // The cart store is only handed to the instance, which reads it on add.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
}

/**
 * Sells a service of the upsell grid per Smart Lock ID, in a modal styled like
 * the notify-me modal. The tile relation carries only the card fields, so the
 * modal loads the full service on open and shows a spinner until it arrives.
 */
export function ServiceNukiIdModal({ product, open, onClose }: Readonly<ServiceNukiIdModalProps>) {
  const t = useTranslations('NotifyMe');
  const tNukiId = useTranslations('NukiIdOverlay');
  const tButton = useTranslations('Button');
  const [state, setState] = useState<ServiceLoadState>({ status: 'loading' });
  const serviceProduct = useServiceProduct(state);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    let isCurrent = true;
    setState({ status: 'loading' });

    getUpsellServiceData(product.path)
      .then((service) => {
        if (isCurrent) {
          setState(service ? { status: 'ready', service } : { status: 'error' });
        }
      })
      .catch((error: unknown) => {
        // The server action logs the cause; the visitor only needs to know
        // that the service cannot be bought right now.
        console.error(`Cannot load the service ${product.path} for the Nuki ID modal`, error);

        if (isCurrent) {
          setState({ status: 'error' });
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [open, product.path]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-60">
      <div className="fixed inset-0 bg-neutral-50 bg-opacity-75 transition-opacity" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <Dialog.Panel
            data-testid="service-nuki-id-modal"
            className="relative w-full transform overflow-hidden rounded-lg bg-white px-6 pb-6 pt-5 text-left shadow-xl transition-all sm:max-w-md sm:p-8"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label={t('Close')}
              className="absolute right-1 top-1 p-1 text-neutral-55 hover:text-neutral-100"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>

            <Dialog.Title as="h3" className="pr-6 text-lg font-semibold text-neutral-100">
              {product.name}
            </Dialog.Title>

            {state.status === 'loading' && (
              <output className="flex justify-center py-8">
                <Spinner className="h-8 w-8" />
                <span className="sr-only">{tButton('loading')}</span>
              </output>
            )}

            {state.status === 'error' && (
              <p className="mt-4 text-sm text-red" role="alert">
                {tNukiId('Unknown Error has occurred')}
              </p>
            )}

            {serviceProduct && (
              <ServiceNukiIdForm
                service={serviceProduct.product}
                validationPath={serviceProduct.validationPath}
                onClose={onClose}
              />
            )}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
}
